// Session-scoped cache for AI suggestion fetches.
// Lets a parent surface (e.g. DraftsReview) prefetch the next item while
// the user is still on the current one — by the time they advance, the
// suggestions are usually already resolved.
//
// Key shape: `${id}:${mode}:${existingFingerprint}`. Including a
// fingerprint of `existingTags` means that adding a tag (which mutates
// existingTags) invalidates a stale prefetch instead of returning
// suggestions that include the tag the user just added.
//
// Mechanism is generic — `mode` is just a string. Today we use
// 'existingTags' / 'newThemes' for ThoughtDetail; any keyed fetcher
// can ride along.

const cache = new Map();

// Cap memory in long-running SPA sessions. Map preserves insertion order
// so the oldest key is `cache.keys().next().value` — pure FIFO eviction.
// We do NOT abort on FIFO eviction: a caller may still be awaiting the
// promise reference they got from us, and aborting would reject it
// underneath them. Eviction just means future lookups miss and re-fetch.
const MAX_CACHE_ENTRIES = 50;

const fingerprint = (existingTags) =>
  Array.isArray(existingTags) ? `${existingTags.length}|${existingTags.join('|')}` : '0';

const keyFor = (id, mode, existingTags) =>
  `${id}:${mode}:${fingerprint(existingTags)}`;

// Folds the hybrid A/B dimension into the cache mode for existingTags fetches.
// KNN and hybrid produce different suggestion sets, so they must key
// separately — otherwise toggling `hybridTags` in the console serves a stale
// list from the other mode. newThemes is unaffected by the hybrid flag.
export const tagSuggestionMode = (mode, hybrid) =>
  mode === 'existingTags' ? `existingTags:${hybrid ? 'hybrid' : 'knn'}` : mode;

const safeAbort = (controller) => {
  try {
    controller.abort();
  } catch (_e) {
    /* ignore */
  }
};

export const cachedSuggestTags = (id, mode, existingTags, fetcher) => {
  const key = keyFor(id, mode, existingTags);
  const existing = cache.get(key);
  if (existing) return existing.promise;

  const controller = new AbortController();
  let raw;
  try {
    raw = Promise.resolve(fetcher(controller.signal));
  } catch (err) {
    return Promise.reject(err);
  }
  const promise = raw.catch((err) => {
    // Evict failed entries so a retry can re-fire. AbortError lands here
    // too (re-thrown from openAiService) so aborted prefetches don't
    // poison the cache as a successful empty result.
    if (cache.get(key)?.controller === controller) {
      cache.delete(key);
    }
    throw err;
  });

  if (cache.size >= MAX_CACHE_ENTRIES) {
    const oldestKey = cache.keys().next().value;
    if (oldestKey !== undefined) cache.delete(oldestKey);
  }
  cache.set(key, { promise, controller });
  return promise;
};

export const prefetchTagSuggestions = (id, mode, existingTags, fetcher) => {
  // Best-effort: swallow rejections so callers don't have to.
  cachedSuggestTags(id, mode, existingTags, fetcher).catch(() => {});
};

export const abortTagSuggestions = (id, mode, existingTags) => {
  const key = keyFor(id, mode, existingTags);
  const entry = cache.get(key);
  if (!entry) return;
  cache.delete(key);
  safeAbort(entry.controller);
};

// Abort and evict every entry matching id+mode regardless of fingerprint.
// Use when a surface knows the (id, mode) pair is no longer needed and
// doesn't want to track the existingTags it was prefetched with.
export const abortTagSuggestionsForId = (id, mode) => {
  const prefix = `${id}:${mode}:`;
  for (const [key, entry] of cache.entries()) {
    if (key.startsWith(prefix)) {
      cache.delete(key);
      safeAbort(entry.controller);
    }
  }
};

// Abort every in-flight request and forget all entries (resolved or not).
// Test-safe — abort failures are swallowed.
export const clearAiSuggestionsCache = () => {
  for (const entry of cache.values()) safeAbort(entry.controller);
  cache.clear();
};

// Test-only inspection hook so regression tests can assert eviction
// without exposing the Map publicly to production callers.
export const __getCacheSizeForTesting = () => cache.size;
